/**
 * Bootstrap + cache the BFF's double-submit CSRF token.
 *
 * The Spring BFF hands out the token at GET /auth/csrf. Spring also
 * sets it as a JS-readable cookie (`XSRF-TOKEN`), so for follow-up
 * requests we can read it from `document.cookie`. We keep a memoised
 * promise so concurrent first-page mutations resolve to the same fetch.
 */
type CsrfPayload = {
  headerName: string | null;
  parameterName: string | null;
  token: string | null;
};

let inFlight: Promise<string | null> | null = null;

export function getCsrfTokenCached(): Promise<string | null> {
  const fromCookie = readCookie("XSRF-TOKEN");
  if (fromCookie) return Promise.resolve(fromCookie);
  if (inFlight) return inFlight;
  inFlight = fetchCsrf();
  return inFlight;
}

export async function refreshCsrfToken(): Promise<string | null> {
  inFlight = fetchCsrf();
  return inFlight;
}

async function fetchCsrf(): Promise<string | null> {
  try {
    const response = await fetch("/auth/csrf", {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    const payload = (await response.json()) as CsrfPayload;
    return payload.token;
  } catch {
    return null;
  } finally {
    inFlight = null;
  }
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) return decodeURIComponent(trimmed.slice(prefix.length));
  }
  return null;
}
